import { Search } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'
import type { ExploreChecklistItem } from './types'

export interface ExploreResultColumnProps {
    items: ExploreChecklistItem[]
    selectedItemId: string
    query: string
    emptyMessage: string
    className?: string
    onQueryChange(query: string): void
    onSelectItem(itemId: string): void
}

/** 탐색 조건으로 좁혀진 체크리스트 결과 목록. */
export function ExploreResultColumn({
    items,
    selectedItemId,
    query,
    emptyMessage,
    className,
    onQueryChange,
    onSelectItem,
}: ExploreResultColumnProps) {
    return (
        <section
            className={cn(
                'bg-card border-border flex min-h-0 min-w-0 flex-col rounded-sm border',
                className
            )}>
            <div className='border-border flex shrink-0 flex-col gap-2 border-b p-3 sm:flex-row sm:items-center sm:justify-between'>
                <div>
                    <h2 className='text-foreground text-sm font-bold'>
                        체크리스트
                    </h2>
                    <p className='text-muted-foreground text-xs'>
                        {items.length}개 항목
                    </p>
                </div>
                <label className='relative block w-full sm:w-64'>
                    <span className='sr-only'>항목 내 검색</span>
                    <Search
                        className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2'
                        aria-hidden='true'
                    />
                    <Input
                        type='search'
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        placeholder='항목 내 검색'
                        className='h-9 pl-9'
                    />
                </label>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto p-2'>
                {items.length > 0 ? (
                    <div className='grid gap-2'>
                        {items.map((item) => {
                            const selected = item.id === selectedItemId
                            return (
                                <button
                                    key={item.id}
                                    type='button'
                                    onClick={() => onSelectItem(item.id)}
                                    className={cn(
                                        'border-border bg-card hover:bg-muted grid w-full gap-2 rounded-sm border p-3 text-left transition-colors',
                                        selected
                                            ? 'border-primary bg-accent'
                                            : ''
                                    )}>
                                    <div className='flex items-start gap-2'>
                                        <span className='text-primary shrink-0 text-sm font-medium'>
                                            {item.itemNumber ?? item.id}.
                                        </span>
                                        <span className='text-foreground min-w-0 flex-1 text-sm leading-6'>
                                            {item.title}
                                        </span>
                                        <Badge tone='info' className='shrink-0'>
                                            0~{item.maxScore}점
                                        </Badge>
                                    </div>
                                    <p className='text-muted-foreground flex flex-wrap items-center gap-1.5 pl-6 text-xs'>
                                        {item.assessmentType ? (
                                            <Badge tone='neutral'>
                                                {item.assessmentType}
                                            </Badge>
                                        ) : null}
                                        <span>
                                            {item.part} ·{' '}
                                            {item.majorCategory ??
                                                '대분류 없음'}{' '}
                                            ·{' '}
                                            {item.middleCategory ??
                                                '중분류 없음'}
                                        </span>
                                    </p>
                                </button>
                            )
                        })}
                    </div>
                ) : (
                    <div className='grid min-h-52 place-items-center px-4 text-center'>
                        <p className='text-muted-foreground text-sm'>
                            {emptyMessage}
                        </p>
                    </div>
                )}
            </div>
        </section>
    )
}
